import Head from 'next/head';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import getConfig from 'next/config';

import { Text, Link } from '@terminal/design-system';
import Layout from '@components/Layout';
import metaRobots from '@helpers/metaRobots';
import sharecardImage from '@public/images/sharecard.jpg';
import sitePath from '@helpers/sitePath';
import { SolutionsForLayout } from '@components/SolutionsForLayout';

const { publicRuntimeConfig } = getConfig();
const { talentHubOnboardingUrl } = publicRuntimeConfig;

const hero = {
  title: {
    main: 'Top developer talent and faster hires for',
    highlight: 'recruiters',
  },
  text: "100K+ vetted developers with broad skills available so you can hire 35% faster than traditional recruiting.  We’re talent and recruiting pros, too, so we're building solutions for recruiting teams to succeed.",
  image: {
    sourceUrl: '/images/solutions-for/recruiters.png',
    altText: 'Man smiling with a dashboard behind him',
    size: {
      width: 551,
      height: 444,
    },
  },
  button: {
    url: talentHubOnboardingUrl,
    text: 'Browse Talent',
    width: '15rem',
  },
};

const offerings = {
  image: {
    src: '/images/solutions-for/work-recruiters.png',
    alt: 'Woman in front of a computer',
  },
  content: [
    {
      title: 'Always-on pipeline means fresh top talent',
      description:
        "We are continuously sourcing through inbound, outbound, and referral channels so you get the best talent who's ready for their next role.",
    },
    {
      title: 'Talent costs 40% less than domestic hiring',
      description:
        'Our global talent is sourced from 9 countries, meaning we can find lower-cost, high-quality talent for your engineering team.',
    },
    {
      title: 'We screen all candidates to only offer the best',
      description:
        'Our in-market talent experts screen each developer for technical skills, English proficiency, and collaboration. We only accept the top 7%.',
    },
    {
      title: 'Deep developer profiles give rich insights',
      description:
        'Our thorough profiles, 1-on-1 vetting, and video interview clips mean you deeply understand all the developers you review.',
    },
  ],
  button: {
    url: talentHubOnboardingUrl,
    text: 'Hire Software Developers',
  },
};

const footer = {
  longText: false,
  text: 'Find the scale you need. Build your team today.',
  image: '/images/home/businesses-footer.jpg',
};

const meta = {
  title: 'Solutions for Recruiters to hire software engineers remotely - Terminal.io',
  description:
    "Recruiters need instant access to top talent to build your teams. Terminal's always-on talent pipeline puts elite software developers at your fingertips.",
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default function ForRecruiters() {
  const router = useRouter();

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        <SolutionsForLayout hero={hero} offerings={offerings} />
      </Layout>
    </>
  );
}
